import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useApi } from '../../api/api';
import './ChokeAnalysis.css';

const ChokeAnalysis = ({ selectedSessionId }) => {
  // State management
  const [aclAnalyses, setAclAnalyses] = useState([]);
  const [selectedAnalysis, setSelectedAnalysis] = useState('');
  const [analysisData, setAnalysisData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('total'); // 'total', 'direct', or 'comparison'

  // API hooks
  const {
    getAclSensitivityAnalyses,
    getAclAnalysisBySession,
  } = useApi();

  const formatCurrency = useCallback((value) => {
    if (!value && value !== 0) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  }, []);

  const formatNumber = useCallback((value) => {
    if (!value && value !== 0) return '0';
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  }, []);

  // Load available ACL sensitivity analyses
  const loadAclAnalyses = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getAclSensitivityAnalyses();
      
      let analysesData = [];
      if (response?.data?.success && response?.data?.results) {
        analysesData = response.data.results;
      } else if (Array.isArray(response?.data)) {
        analysesData = response.data;
      }
      
      setAclAnalyses(analysesData);
      
      // Auto-select the first analysis if available
      if (analysesData.length > 0 && !selectedAnalysis) {
        const firstAnalysis = analysesData[0];
        const analysisKey = firstAnalysis.base_session_name || firstAnalysis.session_name || firstAnalysis.session_id;
        setSelectedAnalysis(analysisKey);
      }
      
    } catch (err) {
      console.error('Error loading ACL analyses:', err);
      setError(`Failed to load ACL analyses: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [getAclSensitivityAnalyses, selectedAnalysis]);

  // Load specific analysis data
  const loadAnalysisData = useCallback(async () => {
    if (!selectedAnalysis) return;
    
    try {
      setLoading(true);
      const response = await getAclAnalysisBySession(selectedAnalysis);
      
      if (response?.data?.success) {
        setAnalysisData(response.data);
        console.log('Analysis data loaded:', response.data);
      } else {
        throw new Error('API response indicates failure');
      }
      
    } catch (err) {
      console.error('Error loading analysis data:', err);
      setError(`Failed to load analysis data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [selectedAnalysis, getAclAnalysisBySession]);

  // Load analyses on component mount
  useEffect(() => {
    loadAclAnalyses();
  }, [loadAclAnalyses]);

  // Load analysis data when selection changes
  useEffect(() => {
    if (selectedAnalysis) {
      loadAnalysisData();
    } else {
      setAnalysisData(null);
    }
  }, [selectedAnalysis, loadAnalysisData]);

  // Calculate heatmap data and colors - DYNAMICALLY HANDLES ANY INCREMENT FORMAT
  const heatmapData = useMemo(() => {
    if (!analysisData?.impact_matrix) return { tableData: [], maxValue: 0, incrementKeys: [] };
    
    console.log('Building heatmap from impact_matrix:', analysisData.impact_matrix);
    
    // DYNAMICALLY EXTRACT INCREMENT KEYS from the data
    const incrementKeysSet = new Set();
    Object.values(analysisData.impact_matrix).forEach(increments => {
      Object.keys(increments).forEach(key => incrementKeysSet.add(key));
    });
    
    // Sort increment keys (handle both MT and % formats)
    const incrementKeys = Array.from(incrementKeysSet).sort((a, b) => {
      // Extract numeric value (handles "-30%", "+10%", "100MT", etc.)
      const aNum = parseFloat(a.replace(/[^\d.-]/g, ''));
      const bNum = parseFloat(b.replace(/[^\d.-]/g, ''));
      return aNum - bNum;
    });
    
    console.log('Detected increment keys:', incrementKeys);
    
    const tableData = [];
    const allTotalImpactValues = [];
    const allDirectValues = [];
    
    // Build table data using dynamic increment keys
    Object.entries(analysisData.impact_matrix).slice(0, 15).forEach(([stockName, increments]) => {
      const row = { stockName, cells: [] };
      
      incrementKeys.forEach((incrementKey) => {
        if (increments[incrementKey]) {
          const economicImpact = increments[incrementKey].economic_impact || {};
          
          const profitDifference = economicImpact.profit_difference || 0;
          const profitPerUnit = economicImpact.profit_per_unit || 0;
          const directSpeciesValue = economicImpact.direct_species_value || 0;
          const economicMultiplier = economicImpact.economic_multiplier || 0;
          const quotaChangeLbs = economicImpact.quota_change_lbs || 0;
          const exVesselPrice = economicImpact.ex_vessel_price_per_lb || 0;
          const unitLabel = economicImpact.unit_label || 'mt';
          
          row.cells.push({
            profitDifference,
            profitPerUnit,
            directSpeciesValue,
            economicMultiplier,
            quotaChangeLbs,
            exVesselPrice,
            unitLabel,
            incrementKey
          });
          
          allTotalImpactValues.push(Math.abs(profitDifference));
          allDirectValues.push(Math.abs(directSpeciesValue));
        } else {
          row.cells.push({
            profitDifference: 0,
            profitPerUnit: 0,
            directSpeciesValue: 0,
            economicMultiplier: 0,
            quotaChangeLbs: 0,
            exVesselPrice: 0,
            unitLabel: 'mt',
            incrementKey
          });
          allTotalImpactValues.push(0);
          allDirectValues.push(0);
        }
      });
      
      tableData.push(row);
    });
    
    const maxTotalImpact = Math.max(...allTotalImpactValues, 1);
    const maxDirectValue = Math.max(...allDirectValues, 1);
    
    console.log('Heatmap data built:', { 
      tableData, 
      maxTotalImpact, 
      maxDirectValue, 
      incrementKeys 
    });
    
    return { 
      tableData, 
      maxTotalImpact, 
      maxDirectValue, 
      incrementKeys 
    };
  }, [analysisData]);

  // Get heatmap color based on value - HANDLES POSITIVE AND NEGATIVE
  const getHeatmapColor = useCallback((value, maxValue) => {
    if (value === 0 || maxValue === 0) return '#ffffff';
    
    const absValue = Math.abs(value);
    const intensity = absValue / maxValue;
    
    // NEGATIVE values = RED (bad for economy)
    if (value < 0) {
      if (intensity > 0.8) return '#7f1d1d'; // Very dark red
      if (intensity > 0.6) return '#991b1b'; // Dark red  
      if (intensity > 0.4) return '#b91c1c'; // Medium red
      if (intensity > 0.2) return '#dc2626'; // Light red
      if (intensity > 0.05) return '#fca5a5'; // Very light red
      return '#fee2e2'; // Lightest red
    }
    
    // POSITIVE values = GREEN (good for economy)
    if (intensity > 0.8) return '#065f46'; // Very dark green
    if (intensity > 0.6) return '#047857'; // Dark green  
    if (intensity > 0.4) return '#059669'; // Medium green
    if (intensity > 0.2) return '#10b981'; // Light green
    if (intensity > 0.05) return '#6ee7b7'; // Very light green
    return '#ecfdf5'; // Lightest green
  }, []);

  // Get text color for readability on heatmap background
  const getTextColor = useCallback((value, maxValue) => {
    if (value === 0 || maxValue === 0) return '#374151';
    
    const absValue = Math.abs(value);
    const intensity = absValue / maxValue;
    return intensity > 0.4 ? '#ffffff' : '#374151';
  }, []);

  // Render cell content based on view mode
  const renderCellContent = useCallback((cell, maxTotalImpact, maxDirectValue) => {
    if (cell.profitDifference === 0 && cell.directSpeciesValue === 0) {
      return <div style={{ color: '#9ca3af' }}>N/A</div>;
    }

    const isChokeSpecies = Math.abs(cell.economicMultiplier) > 15;

    if (viewMode === 'total') {
      // Total Fleet Impact View
      const bgColor = getHeatmapColor(cell.profitDifference, maxTotalImpact);
      const textColor = getTextColor(cell.profitDifference, maxTotalImpact);
      
      return (
        <div style={{ position: 'relative' }}>
          {isChokeSpecies && (
            <div 
              style={{
                position: 'absolute',
                top: '2px',
                right: '2px',
                background: '#ff9800',
                color: 'white',
                padding: '2px 6px',
                borderRadius: '3px',
                fontSize: '10px',
                fontWeight: 'bold'
              }}
              title="High choke effect - large fleet impact from small quota change"
            >
              {cell.economicMultiplier.toFixed(1)}x
            </div>
          )}
          <div style={{ fontWeight: 'bold', fontSize: '14px', color: textColor }}>
            {formatCurrency(cell.profitDifference)}
          </div>
          <div style={{ fontSize: '11px', marginTop: '2px', opacity: 0.9, color: textColor }}>
            {formatCurrency(cell.profitPerUnit)}/{cell.unitLabel}
          </div>
        </div>
      );
    } else if (viewMode === 'direct') {
      // Direct Species Value View
      const bgColor = getHeatmapColor(cell.directSpeciesValue, maxDirectValue);
      const textColor = getTextColor(cell.directSpeciesValue, maxDirectValue);
      
      return (
        <div style={{ position: 'relative' }}>
          {isChokeSpecies && (
            <div 
              style={{
                position: 'absolute',
                top: '2px',
                left: '2px',
                fontSize: '16px'
              }}
              title="Choke species - high economic leverage"
            >
              ⚠️
            </div>
          )}
          <div style={{ fontWeight: 'bold', fontSize: '14px', color: textColor }}>
            {formatCurrency(cell.directSpeciesValue)}
          </div>
          <div style={{ fontSize: '11px', marginTop: '2px', opacity: 0.9, color: textColor }}>
            {formatNumber(cell.quotaChangeLbs)} lbs
          </div>
          <div style={{ fontSize: '10px', marginTop: '1px', opacity: 0.85, color: textColor }}>
            @ ${cell.exVesselPrice.toFixed(3)}/lb
          </div>
        </div>
      );
    } else {
      // Comparison View - show both metrics
      return (
        <div style={{ 
          fontSize: '11px', 
          display: 'flex', 
          flexDirection: 'column', 
          gap: '4px',
          padding: '4px'
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            borderBottom: '1px solid rgba(0,0,0,0.1)',
            paddingBottom: '2px'
          }}>
            <span style={{ fontWeight: '600' }}>Total:</span>
            <span>{formatCurrency(cell.profitDifference)}</span>
          </div>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            borderBottom: '1px solid rgba(0,0,0,0.1)',
            paddingBottom: '2px'
          }}>
            <span style={{ fontWeight: '600' }}>Direct:</span>
            <span>{formatCurrency(cell.directSpeciesValue)}</span>
          </div>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            paddingTop: '2px',
            fontWeight: 'bold',
            color: Math.abs(cell.economicMultiplier) > 15 ? '#ff9800' : 'inherit'
          }}>
            <span>Mult:</span>
            <span>{cell.economicMultiplier.toFixed(1)}x</span>
          </div>
        </div>
      );
    }
  }, [viewMode, getHeatmapColor, getTextColor, formatCurrency, formatNumber]);

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Loading choke stock analysis...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-error">
        <p>{error}</p>
        <button onClick={loadAclAnalyses} className="btn-primary">Retry</button>
      </div>
    );
  }

  if (aclAnalyses.length === 0) {
    return (
      <div className="no-data-section">
        <div className="no-data-message">
          <h3>No ACL Sensitivity Analyses Found</h3>
          <p>No ACL sensitivity analysis sessions found. Please run an ACL sensitivity analysis first to generate choke stock data.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="choke-analysis">
      <div className="choke-analysis-header">
        <h2>Choke Stock Sensitivity Analysis</h2>
        
        {/* Analysis Selection Controls */}
        <div className="dashboard-filters">
          <div className="filter-group">
            <label>ACL Sensitivity Analysis:</label>
            <select 
              value={selectedAnalysis} 
              onChange={(e) => setSelectedAnalysis(e.target.value)}
            >
              <option value="">Choose analysis...</option>
              {aclAnalyses.map(analysis => {
                const key = analysis.base_session_name || analysis.session_name || analysis.session_id;
                const label = `${analysis.base_session_name || analysis.session_name} (${analysis.total_sessions || analysis.sessions?.length || 'N/A'} sessions)`;
                return (
                  <option key={key} value={key}>
                    {label}
                  </option>
                );
              })}
            </select>
          </div>
        </div>

        
      </div>
      <div style={{
        'border': 'solid 1px #e5e7eb',
        'borderRadius': '8px'
      }}>
        {/* View Mode Toggle - Tab Style */}
        {selectedAnalysis && analysisData && (
        <div className="view-mode-tabs" style={{
          display: 'flex',
          gap: '0',
          marginTop: '20px',
          marginBottom: '0',
          borderBottom: '2px solid #e5e7eb'
        }}>
          <button
            onClick={() => setViewMode('total')}
            className={`tab-button ${viewMode === 'total' ? 'active' : ''}`}
          >
            Total Fleet Impact
          </button>
          <button
            onClick={() => setViewMode('direct')}
            className={`tab-button ${viewMode === 'direct' ? 'active' : ''}`}
          >
            Direct Species Value
          </button>
          <button
            onClick={() => setViewMode('comparison')}
            className={`tab-button ${viewMode === 'comparison' ? 'active' : ''}`}
          >
            Comparison
          </button>
        </div>
        )}
        {!selectedAnalysis ? (
          <div className="no-data-section">
            <div className="no-data-message">
              <h3>No Analysis Selected</h3>
              <p>Please select an ACL sensitivity analysis to view choke stock data.</p>
            </div>
          </div>
        ) : !analysisData ? (
          <div className="dashboard-loading">
            <div className="loading-spinner"></div>
            <p>Loading analysis data for: {selectedAnalysis}</p>
          </div>
        ) : (
          <>
            {/* Economic Impact Heatmap Table */}
            <div className="analysis-section" style={{ marginTop: '0' }}>
              <div className="table-container-full" style={{ marginTop: '0' }}>
                {heatmapData.tableData.length > 0 ? (
                  <table className="results-table heatmap-table">
                    <thead>
                      <tr>
                        <th>Stock</th>
                        {heatmapData.incrementKeys.map((key) => (
                          <th key={key}>{key} Change</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {heatmapData.tableData.map((row) => (
                        <tr key={row.stockName}>
                          <td style={{ fontWeight: 'bold', backgroundColor: '#f9fafb' }}>
                            {row.stockName}
                          </td>
                          {row.cells.map((cell, cellIndex) => {
                            // Determine which max value to use for coloring
                            const maxValue = viewMode === 'direct' 
                              ? heatmapData.maxDirectValue 
                              : heatmapData.maxTotalImpact;
                            
                            const colorValue = viewMode === 'direct'
                              ? cell.directSpeciesValue
                              : cell.profitDifference;
                            
                            const bgColor = viewMode === 'comparison' 
                              ? '#ffffff' 
                              : getHeatmapColor(colorValue, maxValue);
                            
                            const textColor = viewMode === 'comparison'
                              ? '#374151'
                              : getTextColor(colorValue, maxValue);
                            
                            const isChokeSpecies = Math.abs(cell.economicMultiplier) > 15;
                            
                            return (
                              <td 
                                key={cellIndex}
                                style={{
                                  backgroundColor: bgColor,
                                  color: textColor,
                                  textAlign: 'center',
                                  padding: viewMode === 'comparison' ? '8px 4px' : '12px 8px',
                                  border: isChokeSpecies && viewMode !== 'comparison' 
                                    ? '2px solid #ff9800' 
                                    : '1px solid #e5e7eb',
                                  boxShadow: isChokeSpecies && viewMode !== 'comparison'
                                    ? '0 0 8px rgba(255, 152, 0, 0.5)'
                                    : 'none',
                                  position: 'relative',
                                  minWidth: viewMode === 'comparison' ? '140px' : 'auto'
                                }}
                              >
                                {renderCellContent(cell, heatmapData.maxTotalImpact, heatmapData.maxDirectValue)}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="no-data-message">
                    <p>No economic impact data available</p>
                    <p style={{ fontSize: '14px', color: '#6b7280', marginTop: '8px' }}>
                      Debug info: {JSON.stringify({
                        hasAnalysisData: !!analysisData,
                        hasImpactMatrix: !!analysisData?.impact_matrix,
                        matrixKeys: analysisData?.impact_matrix ? Object.keys(analysisData.impact_matrix) : []
                      })}
                    </p>
                  </div>
                )}
              </div>
              
              {/* Legend and Instructions */}
              <div style={{ marginTop: '16px', display: 'flex', gap: '24px', alignItems: 'flex-start' }}>
                {/* How to Read - Dynamic based on view mode */}
                <div style={{ flex: 1, padding: '16px', backgroundColor: '#f9fafb', borderRadius: '8px', fontSize: '14px' }}>
                  <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#374151' }}>
                    {viewMode === 'total' && 'Total Fleet Impact - How to Read:'}
                    {viewMode === 'direct' && 'Direct Species Value - How to Read:'}
                    {viewMode === 'comparison' && 'Comparison View - How to Read:'}
                  </div>
                  
                  {viewMode === 'total' && (
                    <ul style={{ margin: 0, paddingLeft: '16px', color: '#6b7280', lineHeight: '1.5' }}>
                      <li><strong>Main value:</strong> Total change in fleet profit</li>
                      <li><strong>Per unit:</strong> Profit per MT or percentage point</li>
                      <li><strong>Orange badge:</strong> Economic multiplier (choke effect)</li>
                      <li><strong>Color intensity:</strong> Darker = larger total impact</li>
                      <li><strong>Orange border:</strong> High choke species (15x+ multiplier)</li>
                    </ul>
                  )}
                  
                  {viewMode === 'direct' && (
                    <ul style={{ margin: 0, paddingLeft: '16px', color: '#6b7280', lineHeight: '1.5' }}>
                      <li><strong>Main value:</strong> Market value of quota change only</li>
                      <li><strong>Quota change:</strong> Pounds of quota added/removed</li>
                      <li><strong>Price:</strong> Ex-vessel price per pound</li>
                      <li><strong>⚠️ symbol:</strong> Choke species warning</li>
                      <li>Compare this to Total Fleet Impact to see choke effect</li>
                    </ul>
                  )}
                  
                  {viewMode === 'comparison' && (
                    <ul style={{ margin: 0, paddingLeft: '16px', color: '#6b7280', lineHeight: '1.5' }}>
                      <li><strong>Total:</strong> Full fleet economic impact</li>
                      <li><strong>Direct:</strong> Just the quota × price value</li>
                      <li><strong>Mult:</strong> Economic multiplier (Total ÷ Direct)</li>
                      <li><strong>High multipliers (15x+):</strong> Shown in orange</li>
                      <li>Reveals how small quota changes drive large fleet impacts</li>
                    </ul>
                  )}
                </div>
                
                {/* Color Legend - Only show for total and direct views */}
                {viewMode !== 'comparison' && (
                  <div style={{ padding: '16px', backgroundColor: '#f9fafb', borderRadius: '8px', fontSize: '14px' }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#374151' }}>Value Scale:</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ width: '20px', height: '16px', backgroundColor: '#065f46', borderRadius: '2px' }}></div>
                        <span style={{ color: '#6b7280' }}>Highest Positive Impact</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ width: '20px', height: '16px', backgroundColor: '#10b981', borderRadius: '2px' }}></div>
                        <span style={{ color: '#6b7280' }}>Moderate Positive Impact</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ width: '20px', height: '16px', backgroundColor: '#ecfdf5', borderRadius: '2px', border: '1px solid #d1d5db' }}></div>
                        <span style={{ color: '#6b7280' }}>Minimal Impact</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ width: '20px', height: '16px', backgroundColor: '#fca5a5', borderRadius: '2px' }}></div>
                        <span style={{ color: '#6b7280' }}>Moderate Negative Impact</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ width: '20px', height: '16px', backgroundColor: '#7f1d1d', borderRadius: '2px' }}></div>
                        <span style={{ color: '#6b7280' }}>Highest Negative Impact</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ChokeAnalysis;